import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import logoCobee from "../../assets/logo-cobee.svg";
import { useAuth } from "../../auth";
import Button from "../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../components/button/Button.types";
import { useMetrics } from "../../metrics";
import classes from "./ApplePayFaq.module.css";
import applePayLogo from "./assets/apple-pay-logo.svg";
import appleWallet from "./assets/apple_wallet.png";
import appleWalletSplitted from "./assets/apple_wallet_splitted.png";
import cobeeApplePayLogo from "./assets/cobee_apple_pay.png";

const ApplePayFaq = () => {
  const { t: translate } = useTranslation();
  const { loginWithRedirect } = useAuth();
  const { trackEvent, pageView } = useMetrics();

  useEffect(() => {
    pageView("Apple Pay FAQ");
  }, [pageView]);

  return (
    <div className={classes.container}>
      <header className="flex w-full items-center justify-between h-16 py-5 px-20">
        <img src={logoCobee} alt={translate("common.logo")} />
        <div className="w[200px]">
          <Button
            text={translate("sign-in.login.button")}
            variant={ButtonVariant.OUTLINE}
            onClick={() => {
              trackEvent("Header Login Button Clicked");
              loginWithRedirect();
            }}
            size={ButtonSize.SMALL}
          />
        </div>
      </header>
      <div className={classes.containerFirstSection}>
        <div className={classes.containerTitle}>
          <h1 className={classes.title}>
            {translate("apple-pay-landing.title")}
          </h1>
          <img
            src={cobeeApplePayLogo}
            alt={translate(
              "apple-pay-landing.accessibility.images.cobee-apple-pay-logo"
            )}
          />
        </div>
        <img
          className={classes.image}
          src={appleWalletSplitted}
          alt={translate("apple-pay-landing.accessibility.images.apple-wallet")}
        />
      </div>
      <div className={classes.containerSecondSection}>
        <div className={classes.section}>
          <h2 className={classes.subtitle}>
            {translate("apple-pay-landing.first-section.col1.title")}
          </h2>
          <p className={classes.description}>
            {translate("apple-pay-landing.first-section.col1.description")}
          </p>
        </div>
        <div className={classes.section}>
          <h2 className={classes.subtitle}>
            {translate("apple-pay-landing.first-section.col2.title")}
          </h2>
          <p className={classes.description}>
            {translate("apple-pay-landing.first-section.col2.description")}
          </p>
        </div>
        <div className={classes.section}>
          <h2 className={classes.subtitle}>
            {translate("apple-pay-landing.first-section.col3.title")}
          </h2>
          <p className={classes.description}>
            {translate("apple-pay-landing.first-section.col3.description-1")}
          </p>
          <p className={classes.description}>
            {translate("apple-pay-landing.first-section.col3.description-2")}
            <img
              src={applePayLogo}
              width={36}
              height={14}
              style={{ display: "inline", margin: "0 2px" }}
            />
            {translate("apple-pay-landing.first-section.col3.description-3")}
          </p>
        </div>
      </div>
      <div className={classes.containerThirdSection}>
        <div className={classes.content}>
          <div className={classes.title}>
            <h2 className={classes.titleText}>
              {translate("apple-pay-landing.second-section.title")}
            </h2>
            <p className={classes.subtitleDescription}>
              {translate("apple-pay-landing.second-section.description")}
            </p>
          </div>
          <div className={classes.features}>
            <div className={classes.section2}>
              <h2 className={classes.subtitle}>
                {translate("apple-pay-landing.second-section.col1.title")}
              </h2>
              <ol
                className={classes.description}
                style={{ listStyleType: "decimal", paddingLeft: "1.5rem" }}
              >
                <li>
                  {translate(
                    "apple-pay-landing.second-section.col1.description-1"
                  )}
                </li>
                <li>
                  {translate(
                    "apple-pay-landing.second-section.col1.description-2"
                  )}
                </li>
                <li>
                  {translate(
                    "apple-pay-landing.second-section.col1.description-3"
                  )}
                </li>
                <li>
                  {translate(
                    "apple-pay-landing.second-section.col1.description-4"
                  )}
                </li>
              </ol>
            </div>
            <div className={classes.section2}>
              <h2 className={classes.subtitle}>
                {translate("apple-pay-landing.second-section.col2.title")}
              </h2>
              <ol
                className={classes.description}
                style={{ listStyleType: "decimal", paddingLeft: "1.5rem" }}
              >
                <li>
                  {translate(
                    "apple-pay-landing.second-section.col2.description-1"
                  )}
                </li>
                <li>
                  {translate(
                    "apple-pay-landing.second-section.col2.description-2"
                  )}
                </li>
                <li>
                  {translate(
                    "apple-pay-landing.second-section.col2.description-3"
                  )}
                </li>
                <li>
                  {translate(
                    "apple-pay-landing.second-section.col2.description-4"
                  )}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className={classes.containerFourthSection}>
        <img
          className={classes.secondImage}
          src={appleWallet}
          alt={translate("apple-pay-landing.accessibility.images.apple-wallet")}
        />
        <div className={classes.fourthSectionRow}>
          <div className={classes.fourthSectionExplanation}>
            <h2 className={classes.subtitle}>
              {translate("apple-pay-landing.third-section.col1.title")}
            </h2>
            <p className={classes.descriptionTitle}>
              {translate("apple-pay-landing.third-section.col1.description-1")}
            </p>
            <p className={classes.description}>
              {translate("apple-pay-landing.third-section.col1.description-2")}
            </p>
            <p className={classes.description}>
              {translate("apple-pay-landing.third-section.col1.description-3")}
            </p>
            <p className={classes.descriptionTitle}>
              {translate("apple-pay-landing.third-section.col1.description-4")}
            </p>
            <p className={classes.description}>
              {translate("apple-pay-landing.third-section.col1.description-5")}
              <img
                src={applePayLogo}
                width={36}
                height={14}
                style={{ display: "inline", margin: "0 0 0 2px" }}
              />
              {translate("apple-pay-landing.third-section.col1.description-6")}
            </p>
          </div>
          <div className={classes.fourthSectionExplanation}>
            <h2 className={classes.subtitle}>
              {translate("apple-pay-landing.third-section.col2.title")}
            </h2>
            <p className={classes.description}>
              {translate("apple-pay-landing.third-section.col2.description")}
              <img
                src={applePayLogo}
                width={36}
                height={14}
                style={{ display: "inline", margin: "0 2px 0 0" }}
              />
              {translate("apple-pay-landing.third-section.col2.description-2")}
            </p>
          </div>
        </div>
      </div>
      <div className={classes.containerFaqSection}>
        <div className={classes.faqQuestion}>
          <h2 className={classes.subtitle}>
            {translate("apple-pay-landing.fourth-section.faq1.title")}
          </h2>
          <p className={classes.description}>
            {translate("apple-pay-landing.fourth-section.faq1.description")}
          </p>
        </div>
        <div className={classes.faqQuestion}>
          <h2 className={classes.subtitle}>
            {translate("apple-pay-landing.fourth-section.faq2.title")}
          </h2>
          <p className={classes.description}>
            {translate("apple-pay-landing.fourth-section.faq2.description-1")}
          </p>
          <p className={classes.description}>
            {translate("apple-pay-landing.fourth-section.faq2.description-2")}
          </p>
          <p className={classes.description}>
            {translate("apple-pay-landing.fourth-section.faq2.description-3")}
          </p>
        </div>
        <div className={classes.faqQuestion}>
          <h2 className={classes.subtitle}>
            {translate("apple-pay-landing.fourth-section.faq3.title")}
          </h2>
          <p className={classes.description}>
            {translate("apple-pay-landing.fourth-section.faq3.description")}
          </p>
        </div>
        <div className={classes.faqQuestion}>
          <h2 className={classes.subtitle}>
            {translate("apple-pay-landing.fourth-section.faq4.title")}
          </h2>
          <p className={classes.description}>
            {translate("apple-pay-landing.fourth-section.faq4.description")}
          </p>
        </div>
        <div className={classes.faqQuestion}>
          <h2 className={classes.subtitle}>
            {translate("apple-pay-landing.fourth-section.faq5.title")}
          </h2>
          <p className={classes.description}>
            {translate("apple-pay-landing.fourth-section.faq5.description")}
          </p>
        </div>
        <div className={classes.faqQuestion}>
          <h2 className={classes.subtitle}>
            {translate("apple-pay-landing.fourth-section.faq6.title")}
          </h2>
          <p className={classes.description}>
            {translate("apple-pay-landing.fourth-section.faq6.description")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ApplePayFaq;
